import {
  filterToCondition,
  type FilterDefinition,
} from "@/shared/components/filters";
import { type Filter, filterSchema } from "@/shared/domain/query/filters";
import { type Condition } from "@/types";

import { conditionToFilters } from "../Trigger/triggerConditionFilters";

/**
 * The form's condition. Rows are the cards the author edits, every one of
 * which must match. A stored condition the cards cannot show — nesting, NOT,
 * an OR of clauses — is kept whole and sent back untouched.
 */
export type ConditionValues =
  { type: "rows"; rows: Filter[] } | { type: "stored"; condition: Condition };

export function emptyConditionValues(): ConditionValues {
  return { type: "rows", rows: [] };
}

/**
 * Opens a stored condition as rows, typed against `definitions`. Without them
 * each field's type is read off its stored value, so the rows can be retyped
 * once the definitions arrive.
 */
export function conditionToValues(
  condition: Condition | null,
  definitions: readonly FilterDefinition[] = [],
): ConditionValues {
  if (condition === null) {
    return emptyConditionValues();
  }
  const projected = conditionToFilters(condition, definitions);
  if (
    projected === null ||
    (projected.matchMode === "OR" && projected.filters.length > 1)
  ) {
    return { type: "stored", condition };
  }
  return { type: "rows", rows: projected.filters };
}

/**
 * The condition the values send; every row is taken as complete.
 *
 * The top-level AND is the list of rows, and a nested group is one row, so
 * the condition reopens as the rows it was sent from (`conditionToFilters`).
 * Several rows are that AND already. A lone row that is itself an AND group (a
 * none-of list with "Not set", a BETWEEN, a multi-value NOT_CONTAINS) is
 * wrapped in a one-child AND, or it would reopen as one row per clause. A lone
 * leaf or OR group (an any-of list, a none-of list without "Not set") is sent
 * bare: several rows never make a top-level OR.
 *
 * Every trigger field takes null checks, so no definitions are needed to send
 * the rows (see `FilterToConditionOptions.definitions`).
 */
export function valuesToCondition(values: ConditionValues): Condition | null {
  if (values.type === "stored") {
    return values.condition;
  }
  const condition = filterToCondition(values.rows, { matchMode: "AND" });
  if (condition === undefined) {
    return null;
  }
  return values.rows.length === 1 &&
    "operator" in condition &&
    condition.operator === "AND"
    ? { operator: "AND", conditions: [condition] }
    : condition;
}

/** Whether a row has everything its comparator needs. */
export function isCompleteRow(row: Filter): boolean {
  return filterSchema.safeParse(row).success;
}

/**
 * The condition the finished rows amount to, for what reads the form while it
 * is being filled in: a row still missing its value is left out.
 */
export function completeCondition(values: ConditionValues): Condition | null {
  if (values.type === "stored") {
    return values.condition;
  }
  return valuesToCondition({
    type: "rows",
    rows: values.rows.filter(isCompleteRow),
  });
}
